import React, { ComponentType, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, omit } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useParams, useSearchParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import { CASH_FLOW_LEDGER_LOCKED_MESSAGE, CASH_FLOW_LEDGER_SLUG } from 'common/constants/cap-table';
import { ENTITY_QUERY_PARAM } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, CustomContainer } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { ConfirmationDeleteWithList, ConfirmationDialog } from 'components/Dialogs';
import { GridSkeleton } from 'components/Grid';
import type { SpreadsheetSelectionSnapshot } from 'components/ScalarDatasheet/ScalarDatasheet.props';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import DialogContext from 'context/DialogContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useLayoutContext } from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { useExportSignal } from 'features/pdfExport';
import type { EvaluatedCell, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { CashFlowsProps } from 'pages/CapTable/cash-distributions/CashFlows.props';
import { useAllSecurityOptions } from 'pages/CapTable/cash-distributions/hooks/useAllSecurityOptions';
import { useEntityOptions } from 'pages/CapTable/cash-distributions/hooks/useEntityOptions';
import { useOwnershipOptions } from 'pages/CapTable/cash-distributions/hooks/useOwnershipOptions';
import useCapTableTertiaryNavItems from 'pages/CapTable/useCapTableTertiaryNavItems';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useTableValidation } from 'services/hooks';
import { useCaptableCashFlowLedger } from 'services/hooks/captable';
import { Logger } from 'utilities';
import { CashFlowSummary } from './components/CashFlowSummary';
import { EntityFilterChips } from './components/EntityFilterChips';
import { FundFilterMenu } from './components/FundFilterMenu';
import { SecurityFilterMenu } from './components/SecurityFilterMenu';
import CashFlowContext from './context/CashFlowContext';
import { alignPreFxSigns } from './data/cashFlowSign';
import { CASH_FLOW_TYPE, CashFlowColumnAlias } from './data/constants';
import { fromEntityValue, isRowInEntityFilter, LedgerEntity, NO_ENTITY_FILTER } from './data/entityValue';
import { isRowInFundFilter, isRowInSecurityFilter } from './data/fundSecurityFilter';
import { CashFlowLedgerItem, CashFlowSourceKind } from './data/types';
import { useCashDistributionsSpreadsheetConfig } from './hooks/useCashFlowLedgerSpreadsheetConfig';
import useSelectionSpaceShortcut from './hooks/useSelectionSpaceShortcut';
// `useGrossIRRSummary` (client-side xirr) is intentionally kept on disk but
// no longer wired to the Cash Flow Summary table. Server-side
// `useServerGrossIRRSummary` calls the captable gross-irrs endpoint so the
// CFL summary IRR is byte-identical to waterfall / fund summary / firm
// summary IRRs (single source of truth in `cap_table.services.gross_irr`).
import { useServerGrossIRRSummary } from './hooks/useServerGrossIRRSummary';
import {
  buildCflSelectionActions,
  cflCellIdentity,
  CflSelectedRow,
  collectSelectedRows,
  partitionExclusionTargets,
  resolveSpaceToggle,
  splitDeletable,
} from './utils/cflSelectionActions';
import { persistVirtualExclusions } from './utils/persistVirtualExclusions';
import { removeRowsByIndex } from './utils/removeRowsByIndex';

// Blank manual row appended by "Add Transaction", booked on the given entity. It starts as an
// Investment so the sign rule applied on save matches what the row shows once it reloads.
const makeEmptyCashFlowRow = (entity: LedgerEntity | null): CashFlowLedgerItem => ({
  ...entity,
  date_of_transaction: '',
  amount: 0,
  transaction_type: '',
  cash_flow_type: CASH_FLOW_TYPE.INVESTED_CAPITAL,
  is_virtual: false,
  is_excluded_from_calculations: false,
});

type LedgerIdentity = {
  captableId: number;
  currency?: string | null;
};

const withEffectiveCurrency = (
  flows: CashFlowLedgerItem[],
  currency?: string | null
) => currency
  ? flows.map(flow => flow.currency ? flow : { ...flow, currency })
  : flows;

const isSameLedger = (left: LedgerIdentity | null, right: LedgerIdentity) =>
  left?.captableId === right.captableId && left.currency === right.currency;

const HIDDEN_ROW_ERROR_MESSAGE =
  'A transaction hidden by a filter needs fixing, so every row is shown again. Press Save to highlight it.';

type LedgerSheet = SpreadsheetConfig<object, object, object, object>;
type SheetRow = CashFlowLedgerItem & { isVisible?: boolean; isDeletableRow?: boolean };

// Ledger rows the sheet was built from, below its header row.
const sheetRows = (sheet: LedgerSheet) => sheet.rowConfig.slice(1) as unknown as SheetRow[];

// The sheet's rows with the grid's unsaved edits. Rows and edits come from the same sheet, so they
// pair up in row order (reverseParse skips virtual rows, which cannot be edited).
const readSheetRows = (sheet: LedgerSheet): CashFlowLedgerItem[] => {
  const edits: CashFlowLedgerItem[] = sheet.reverseParse();
  return sheetRows(sheet).map(row => {
    const item = omit(row, ['isVisible', 'isDeletableRow']);
    return item.is_virtual ? item : { ...item, ...edits.shift() };
  });
};

const CashFlows = ({
  capTableVersionsByCompany,
  isActiveTab = true,
}: CashFlowsProps) => {
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const capTableNavItems = useCapTableTertiaryNavItems(firmSlugParam ?? '', companySlugParam ?? '', capTableVersionsByCompany);

  const { fetchCashFlowLedger, updateCashFlowLedger } = useCaptableCashFlowLedger();
  const { showDialog } = useContext(DialogContext);
  const { setAction: setUnsavedChangesAction } = useContext(UnsavedChangesContext);
  const {
    updatePageActions,
    togglePdfDialog,
    toggleMainForm,
    viewOnlyUser,
    isExportMode,
  } = useLayoutContext();
  const [{ captableInfo, fundList, companyInfo, firmInfo }] = useStore() as unknown as UseStoreValues;
  const [cashFlows, setCashFlows] = useState<CashFlowLedgerItem[]>([]);
  const [hasFetchedLedger, setHasFetchedLedger] = useState(false);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  const [selectionSnapshot, setSelectionSnapshot] = useState<SpreadsheetSelectionSnapshot | null>(null);
  const [sheetContainer, setSheetContainer] = useState<HTMLDivElement | null>(null);
  const effectiveIrrCurrency = companyInfo?.irr_currency ?? companyInfo?.captable_currency;
  const ledgerRequestGenerationRef = useRef(0);
  const appliedLedgerIdentityRef = useRef<LedgerIdentity | null>(null);
  const currentLedgerIdentityRef = useRef<LedgerIdentity | null>(null);
  currentLedgerIdentityRef.current = captableInfo?.id
    ? { captableId: captableInfo.id, currency: effectiveIrrCurrency }
    : null;

  const sortCashFlowsByDate = useCallback(
    (flows: CashFlowLedgerItem[]) =>
      [...flows].sort((a, b) => {
        const dateA = a.date_of_transaction ? new Date(a.date_of_transaction).getTime() : 0;
        const dateB = b.date_of_transaction ? new Date(b.date_of_transaction).getTime() : 0;
        return dateA - dateB;
      }),
    []
  );

  // Merge current spreadsheet data with persisted data
  const mergeSpreadsheetDataWithPersisted = useCallback((
    spreadsheetData: CashFlowLedgerItem[],
    persistedFlows: CashFlowLedgerItem[]
  ) => {

    // Create a map of reversed data by ID (for items that have IDs)
    // Using string keys to avoid number/string type mismatch during comparison
    const reversedById = new Map<string, CashFlowLedgerItem>();
    const newItems: CashFlowLedgerItem[] = [];

    spreadsheetData.forEach(item => {
      if (item.id) {
        reversedById.set(String(item.id), item);
      } else {
        newItems.push(item);
      }
    });

    // Only keep persisted items that are saved (id) or virtual.
    const persistedItems = persistedFlows.filter(item => item.id || item.is_virtual);

    // We use a shallow merge here to preserve rich fields from the database with
    // the live spreadsheet state (like unsaved exclusion toggles or edited amounts).
    const mergedItems = persistedItems.map(persistedItem => {
      const stringId = persistedItem.id ? String(persistedItem.id) : null;
      if (stringId && reversedById.has(stringId)) {
        return { ...persistedItem, ...reversedById.get(stringId) };
      }
      // Keep the original persisted item
      return persistedItem;
    });

    // Add any new items (without IDs) from the spreadsheet
    return [...mergedItems, ...newItems];
  }, []);

  const { securityOptionsByEntity, allowedSecurityOptionsByEntity, loading: securityOptionsLoading } = useAllSecurityOptions({
    companyInfo,
    captableInfo,
    capTableVersionsByCompany,
  });

  // Placeholder ref for spreadsheet - will be populated after config is created
  const spreadsheetRef = useRef<typeof spreadsheet>(null);

  // The sheet on screen and the rows it was built from; null while a newer sheet is still being evaluated.
  const shownGridRef = useRef<{ sheet: LedgerSheet; rows: CashFlowLedgerItem[] } | null>(null);

  // Applies a row change, carrying the grid's unsaved edits along. When the grid is behind state (a newer
  // change is still drawing), state already holds those edits and the change gets no sheet.
  const changeRows = useCallback(
    (change: (rows: CashFlowLedgerItem[], sheet?: LedgerSheet) => CashFlowLedgerItem[]) => {
      const grid = shownGridRef.current;
      setCashFlows(rows => (grid && rows === grid.rows ? change(readSheetRows(grid.sheet), grid.sheet) : change(rows)));
    },
    []
  );

  // Rows shown are picked by entity chips kept in the `entity` query param. Chips appear only for companies with
  // acquired companies, and export mode always shows the full ledger. Any URL change to the filter rebuilds the
  // grid, so unsaved edits move into state first.
  const entityOptions = useEntityOptions({ companyInfo });
  const showEntityFilter = !isExportMode && entityOptions.some(({ type }) => type === 'alias');
  const requestedEntities = searchParams.getAll(ENTITY_QUERY_PARAM);
  const requestedEntityKey = showEntityFilter
    ? entityOptions.map(({ value }) => value).filter(value => requestedEntities.includes(value)).join(',')
    : '';
  const [activeEntityKey, setActiveEntityKey] = useState(requestedEntityKey);
  const activeEntities = useMemo(() => (activeEntityKey ? activeEntityKey.split(',') : NO_ENTITY_FILTER), [activeEntityKey]);
  useEffect(() => {
    if (requestedEntityKey !== activeEntityKey) {
      changeRows(rows => rows);
      setActiveEntityKey(requestedEntityKey);
    }
  }, [activeEntityKey, changeRows, requestedEntityKey]);

  const writeEntityParam = useCallback(
    (entities: string[]) =>
      setSearchParams(
        current => {
          const next = new URLSearchParams(current);
          next.delete(ENTITY_QUERY_PARAM);
          entities.forEach(entity => next.append(ENTITY_QUERY_PARAM, entity));
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const toggleEntity = useCallback(
    (value: string) =>
      writeEntityParam(
        activeEntities.includes(value) ? activeEntities.filter(entity => entity !== value) : [...activeEntities, value]
      ),
    [activeEntities, writeEntityParam]
  );

  // Fund/security filters: display-only, default to everything, also drive the Gross IRR table's
  // investments/proceeds sums. A one-shot pulse (see handleSave) resets both back to "all selected"
  // when a row they hide fails validation.
  const [activeFundIds, setActiveFundIds] = useState<string[]>([]);
  const [activeSecurityIds, setActiveSecurityIds] = useState<string[]>([]);
  const [resetFundSecurityFilters, setResetFundSecurityFilters] = useState(false);
  useEffect(() => {
    if (resetFundSecurityFilters) {
      setResetFundSecurityFilters(false);
    }
  }, [resetFundSecurityFilters]);

  // The delete button counts rendered rows from 1, skipping rows hidden by the entity filter. A click
  // on a grid that is still redrawing is ignored, since its row positions are out of date.
  const handleDeleteRow = useCallback(
    (renderedRowIndex: number) =>
      changeRows((rows, sheet) => {
        if (!sheet) {
          return rows;
        }
        const shownIndexes = sheetRows(sheet).flatMap(({ isVisible }, index) => (isVisible === false ? [] : [index]));
        return rows.filter((_, index) => index !== shownIndexes[renderedRowIndex - 1]);
      }),
    [changeRows]
  );

  // Bulk delete takes ledger rows of `fromSheet` counted from 1 with hidden rows included, unlike the button's
  // rendered rows. If the sheet on screen is no longer `fromSheet`, its positions may be stale, so nothing is removed.
  const handleDeleteRows = useCallback(
    (rowIndexes: number[], fromSheet: LedgerSheet) =>
      changeRows((rows, sheet) => (sheet && sheet === fromSheet ? removeRowsByIndex(rows, rowIndexes) : rows)),
    [changeRows]
  );

  // Live FX preview writeback. Routes through the workbook's `onChange`
  // (rather than `setCells`) because the spreadsheet renders from
  // `sheet.cells` - the SpreadsheetConfig's INTERNAL cell map - not from
  // useWorkbook's React state. `onChange` runs the full update path
  // (`sheet.afterCellChanged` -> `cell.onChange` -> validation), so the
  // converted amount lands in both the framework's internal state and
  // the rendered DOM. Going through `setCells` alone would update the
  // outer state but leaves the sheet's internal cells stale, so the cell
  // would never actually display the new value.
  //
  // Cells + onChange come from `useWorkbook`, which runs AFTER this hook
  // chain (it needs `spreadsheets` which needs this callback). Forward-
  // declare refs that get populated by the effect blocks below `useWorkbook`,
  // so the callback's identity stays stable across renders and the parser
  // wrapper in `useCashDistributionsSpreadsheetConfig` doesn't churn.
  const cellsRef = useRef<any>(undefined);
  const onChangeRef = useRef<((cell: any, value: any) => void) | null>(null);

  const handlePreFxHostPreview = useCallback((cellKey: string, value: number | null) => {
    const sheetCells = cellsRef.current?.CashDistributions;
    const targetCell = sheetCells?.[cellKey];
    if (!targetCell || !onChangeRef.current) {
      return;
    }
    onChangeRef.current(targetCell, value);
  }, []);

  const { spreadsheet } = useCashDistributionsSpreadsheetConfig({
    ledgerData: cashFlows,
    activeEntities,
    activeFundIds,
    activeSecurityIds,
    companyId: companyInfo?.id,
    isDisabled,
    securityOptionsByEntity,
    onDeleteRow: handleDeleteRow,
    onPreFxHostPreview: handlePreFxHostPreview,
  });

  // Update spreadsheet ref when spreadsheet changes
  useEffect(() => {
    spreadsheetRef.current = spreadsheet;
  }, [spreadsheet]);

  const ownershipOptions = useOwnershipOptions({ companyInfo, fundList });

  const cashFlowContextValue = useMemo(
    () => ({
      ownershipOptions,
      entityOptions,
      securityOptionsByEntity,
      allowedSecurityOptionsByEntity,
      securityOptionsLoading,
    }),
    [ownershipOptions, entityOptions, securityOptionsByEntity, allowedSecurityOptionsByEntity, securityOptionsLoading]
  );

  const spreadsheets = useMemo(() => (spreadsheet ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, cells, workbook, workbookContextValue, data, setCells, evaluatedSpreadsheets } = useWorkbook(spreadsheets);
  shownGridRef.current = evaluatedSpreadsheets?.[0] === spreadsheet ? { sheet: spreadsheet, rows: cashFlows } : null;
  const { validateCells } = useTableValidation();

  // Populate the refs forward-declared above so `handlePreFxHostPreview`
  // can reach `onChange` / `cells` without making them React deps that
  // would invalidate the parser's wrapped callback on every render.
  useEffect(() => {
    cellsRef.current = cells;
  }, [cells]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Reloads the ledger after a virtual-row PATCH so the sheet and the IRR
  // summary pick up the new flag; a stale or superseded response is dropped.
  const captableId = captableInfo?.id;
  const refetchLedgerAfterVirtualToggle = useCallback(async () => {
    if (!captableId) {
      return;
    }
    const identity = { captableId, currency: effectiveIrrCurrency };
    if (!isSameLedger(currentLedgerIdentityRef.current, identity)) {
      return;
    }
    const generation = ++ledgerRequestGenerationRef.current;
    const flows = await fetchCashFlowLedger(captableId);
    if (
      generation !== ledgerRequestGenerationRef.current
      || !isSameLedger(currentLedgerIdentityRef.current, identity)
    ) {
      return;
    }
    const freshFlows = withEffectiveCurrency(
      (flows || []) as unknown as CashFlowLedgerItem[],
      effectiveIrrCurrency
    );
    const currentSpreadsheet = spreadsheetRef.current;
    setCashFlows(sortCashFlowsByDate(
      currentSpreadsheet
        ? mergeSpreadsheetDataWithPersisted(currentSpreadsheet.reverseParse() as CashFlowLedgerItem[], freshFlows)
        : freshFlows
    ));
  }, [captableId, effectiveIrrCurrency, fetchCashFlowLedger, mergeSpreadsheetDataWithPersisted, sortCashFlowsByDate]);

  // Virtual (fund-derived) rows cannot round-trip through bulk save, so an
  // exclusion toggle on one PATCHes its fund source right away.
  const persistVirtualExclusion = useCallback(
    (sourceKind: CashFlowSourceKind, sourceId: number, isExcluded: boolean) =>
      persistVirtualExclusions([{ sourceKind, sourceId, isExcluded }], refetchLedgerAfterVirtualToggle),
    [refetchLedgerAfterVirtualToggle]
  );

  // Bulk exclusion toggle. Manual rows take the checkbox's own cell mutation plus its
  // change notification so Save carries the flag; fund-derived rows PATCH their source.
  // Refreshing the workbook cells afterwards is what makes the sheet repaint the struck
  // amounts and re-read the selection, so the bar offers the opposite action next.
  const setRowsExcluded = useCallback(
    (rows: CflSelectedRow[], isExcluded: boolean) => {
      const { manual, virtual } = partitionExclusionTargets(rows, isExcluded);
      manual.forEach(cell => {
        cell.setProps?.({ enabled: !isExcluded, ignoreExpr: isExcluded });
        onChange(cell as EvaluatedCell, cell.expr);
        (cell as EvaluatedCell).dispatchEvent?.();
      });
      if (manual.length) {
        setCells(prev => (prev ? { ...prev } : prev));
        // Same unsaved-changes guard a checkbox click arms, so leaving the tab warns.
        // The provider types both slots as prop-less, hence the cast on the dialog shell.
        setUnsavedChangesAction({
          wrapper: ConfirmationDialog as ComponentType<unknown>,
          content: ConfirmUnsavedChanges,
        });
      }
      if (virtual.length) {
        persistVirtualExclusions(virtual, refetchLedgerAfterVirtualToggle);
      }
    },
    [onChange, refetchLedgerAfterVirtualToggle, setCells, setUnsavedChangesAction]
  );

  const getSelectedRows = useCallback(
    (snapshot: SpreadsheetSelectionSnapshot | null) =>
      collectSelectedRows(snapshot, (spreadsheet?.data ?? []) as ICell[][]),
    [spreadsheet]
  );

  // One confirmation for the rows the bar acted on: the manual rows that go, and the fund-derived rows kept.
  // Confirming also clears the selection, so rows that move up into the gap are never acted on next.
  const confirmDeleteRows = useCallback(
    (rows: CflSelectedRow[], clearSelection: () => void) => {
      const { rowIndexes, kept } = splitDeletable(rows);
      if (!rowIndexes.length) {
        return;
      }
      const grid = (spreadsheet?.data ?? []) as ICell[][];
      const labelOf = (cells: ICell[], alias: CashFlowColumnAlias) => {
        const cell = cells.find(candidate => candidate.alias === alias);
        const shown = cell?.value ?? cell?.expr;
        return shown == null || shown === '' ? '' : String(shown);
      };
      const messages = rowIndexes.map(rowIndex => {
        const cells = grid.find(row => row.some(cell => cell.rowNumber === rowIndex + 1)) ?? [];
        const details = [
          labelOf(cells, CashFlowColumnAlias.DATE_OF_TRANSACTION),
          labelOf(cells, CashFlowColumnAlias.TRANSACTION_TYPE),
        ].filter(Boolean).join(' - ');
        return details ? `Row ${rowIndex}: ${details}` : `Row ${rowIndex}`;
      });
      if (kept) {
        messages.push(`Note: ${kept} fund-derived row${kept === 1 ? '' : 's'} cannot be deleted and will be kept.`);
      }
      const count = rowIndexes.length;
      showDialog({
        wrapper: ConfirmationDialog,
        wrapperProps: { title: `Delete ${count} cash flow row${count === 1 ? '' : 's'}?` },
        content: ConfirmationDeleteWithList,
        contentProps: { itemName: 'selection', messages },
        actions: [
          { label: 'Cancel', type: 'cancel' },
          {
            label: `Delete ${count}`,
            variant: 'contained',
            type: 'danger',
            callback: () => {
              handleDeleteRows(rowIndexes, spreadsheet);
              clearSelection();
            },
          },
        ],
      });
    },
    [handleDeleteRows, showDialog, spreadsheet]
  );

  const selectionActions = useCallback(
    (snapshot: SpreadsheetSelectionSnapshot | null) =>
      buildCflSelectionActions(getSelectedRows(snapshot), {
        canEdit: !isDisabled,
        onSetExcluded: setRowsExcluded,
        onDeleteRows: confirmDeleteRows,
      }),
    [confirmDeleteRows, getSelectedRows, isDisabled, setRowsExcluded]
  );

  // Space over a multi-row selection runs the bulk toggle; a single cell keeps its own checkbox.
  const toggleSelectionExclusion = useCallback(() => {
    const toggle = resolveSpaceToggle(getSelectedRows(selectionSnapshot));
    if (!toggle) {
      return false;
    }
    setRowsExcluded(toggle.rows, toggle.isExcluded);
    return true;
  }, [getSelectedRows, selectionSnapshot, setRowsExcluded]);

  useSelectionSpaceShortcut({
    container: sheetContainer,
    // Resolved every render: a rebuilt sheet only fills in its cells after it first renders.
    enabled: isActiveTab && !isDisabled && getSelectedRows(selectionSnapshot).length > 1,
    onSpace: toggleSelectionExclusion,
  });

  // Wraps useWorkbook's onChange. Real-row toggles flow through the existing
  // bulk-save-on-Save flow (reverse parser carries `is_excluded_from_calculations`).
  // Virtual-row Amount toggles fire a single FO source PATCH immediately.
  // An Amount cell also refreshes the workbook cells, the same nudge the bulk toggle
  // gives it, so the selection bar re-reads a row its own checkbox just changed.
  const handleChange = useCallback(
    (cell: any, value: any, customFn?: any) => {
      const isAmountCell = cell?.alias === CashFlowColumnAlias.AMOUNT;
      if (isAmountCell && cell?.is_virtual && cell?.source_kind && cell?.source_id !== undefined) {
        persistVirtualExclusion(cell.source_kind, cell.source_id, !cell.enabled);
      }
      const result = onChange(cell, value, customFn);
      if (isAmountCell) {
        setCells(prev => (prev ? { ...prev } : prev));
      }
      return result;
    },
    [onChange, persistVirtualExclusion, setCells]
  );

  // Note: We intentionally do NOT sync spreadsheet data to cashFlows on every edit
  // because that causes the spreadsheet config to be recreated, causing re-renders.
  // The IRR calculation will use slightly stale data until the next save/refresh,
  // but this is acceptable for better UX.

  // Investments/proceeds are summed client-side from whatever `cashFlows` this hook gets, so
  // filtering its input here keeps those sums limited to the selected funds/securities. Gross IRR
  // itself still comes untouched from the gross-irrs endpoint (see `owners` below) - recomputing
  // it client-side would use a different methodology and could change a fund's IRR even when
  // that fund itself was never deselected.
  const fundSecurityFilteredCashFlows = useMemo(
    () => cashFlows.filter(flow => isRowInFundFilter(flow, activeFundIds) && isRowInSecurityFilter(flow, activeSecurityIds)),
    [activeFundIds, activeSecurityIds, cashFlows]
  );
  const { owners: serverOwners, firm, ownerNames } = useServerGrossIRRSummary({
    captableId,
    fundList,
    companyInfo,
    cashFlows: fundSecurityFilteredCashFlows,
    ownershipOptions,
    // Refetch IRR when the ledger is re-denominated or its contents change.
    // Length alone misses irr_currency recomputes and exclusion toggles.
    refreshKey: `${companyInfo?.irr_currency ?? ''}:${cashFlows
      .map(flow => `${flow.id ?? 'n'}:${flow.amount}:${flow.is_excluded_from_calculations ? 1 : 0}`)
      .join('|')}`,
  });

  // A fund the user fully deselected would otherwise still show its (unfiltered) server Gross IRR
  // alongside zeroed investments/proceeds, so drop it from the table instead. Security-only
  // narrowing never removes an owner row (there's no equivalent security-subset IRR to show).
  const owners = useMemo(
    () => Object.fromEntries(
      Object.entries(serverOwners).filter(([token]) => {
        const fundId = token.match(/^fund-(\d+)$/)?.[1];
        return !fundId || !activeFundIds.length || activeFundIds.includes(fundId);
      })
    ),
    [serverOwners, activeFundIds]
  );

  // A fetch may finish after a newer cap-table/currency request. Only the
  // latest generation can update the ledger, and its identity determines
  // whether spreadsheet-only rows belong to the result being applied.
  // Refetch cash flow ledger when the tab becomes active (to pick up changes
  // from Fund Ownership) or when the company IRR currency changes (the backend
  // re-denominates the ledger, so the current rows are stale).
  useEffect(() => {
    const generation = ++ledgerRequestGenerationRef.current;
    if (isActiveTab && captableId) {
      const identity: LedgerIdentity = {
        captableId,
        currency: effectiveIrrCurrency,
      };
      fetchCashFlowLedger(captableId).then(flows => {
        if (generation !== ledgerRequestGenerationRef.current) {
          return;
        }

        const freshFlows = withEffectiveCurrency(
          (flows || []) as unknown as CashFlowLedgerItem[],
          effectiveIrrCurrency
        );
        const appliedIdentity = appliedLedgerIdentityRef.current;
        const sameCaptable = appliedIdentity?.captableId === captableId;
        const currencyChanged = sameCaptable
          && appliedIdentity.currency !== effectiveIrrCurrency;
        appliedLedgerIdentityRef.current = identity;
        const currentSpreadsheet = spreadsheetRef.current;
        if (!currentSpreadsheet || !sameCaptable) {
          setCashFlows(sortCashFlowsByDate(freshFlows));
        } else if (currencyChanged) {
          // Re-denominated server values win; keep only unsaved manual rows
          // (reverseParse skips virtual rows, so id-less rows are user-added).
          const newItems = withEffectiveCurrency(
            (currentSpreadsheet.reverseParse() as CashFlowLedgerItem[]).filter(item => !item.id),
            effectiveIrrCurrency
          );
          setCashFlows(sortCashFlowsByDate([...freshFlows, ...newItems]));
        } else {
          const reversedData = currentSpreadsheet.reverseParse() as CashFlowLedgerItem[];
          setCashFlows(sortCashFlowsByDate(mergeSpreadsheetDataWithPersisted(reversedData, freshFlows)));
        }
        setHasFetchedLedger(true);
      });
    }

    return () => {
      if (ledgerRequestGenerationRef.current === generation) {
        ledgerRequestGenerationRef.current += 1;
      }
    };
  }, [
    captableId,
    effectiveIrrCurrency,
    fetchCashFlowLedger,
    mergeSpreadsheetDataWithPersisted,
    sortCashFlowsByDate,
    isActiveTab,
  ]);

  let cashFlowExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!spreadsheet || !hasFetchedLedger) {
    cashFlowExportStatus = 'loading';
  } else if (isEmpty(cashFlows)) {
    cashFlowExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.CASH_FLOW_LEDGER.id,
    status: cashFlowExportStatus,
    enabled: isExportMode,
  });

  // Check allocation finalization status on mount
  useEffect(() => {
    if (captableInfo?.id) {
      const verifyAllocationStatus = async () => {
        try {
          const allocations = (await captableAction.verifyIfHasFinalAllocation(captableInfo.id!)) as FinalAllocation[];

          setIsAllocationFinal(!isEmpty(allocations));
          setIsDisabled(!isEmpty(allocations) || viewOnlyUser);
          setFinalAllocations(allocations);

          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
        } catch (error) {
          Logger.error('Error verifying allocation status:', error);
        }
      };
      verifyAllocationStatus();
    }
  }, [captableInfo?.id, viewOnlyUser, isExportMode]);

  // New rows take the single selected entity, else the company. A filter that would hide them is cleared.
  const handleAddMultipleRows = useCallback(
    (numRows = 1) => {
      const defaultEntity = activeEntities.length === 1 ? activeEntities[0] : `company-${companyInfo?.id}`;
      const newRows = Array.from({ length: numRows }, () =>
        makeEmptyCashFlowRow(fromEntityValue(defaultEntity, companyInfo?.id))
      );
      changeRows(rows => withEffectiveCurrency([...rows, ...newRows], effectiveIrrCurrency));
      if (!isRowInEntityFilter(newRows[0], activeEntities)) {
        writeEntityParam(NO_ENTITY_FILTER);
      }
    },
    [activeEntities, changeRows, companyInfo?.id, effectiveIrrCurrency, writeEntityParam]
  );

  const handleAddRow = useCallback(() => handleAddMultipleRows(1), [handleAddMultipleRows]);

  const handleSave = useCallback(async () => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet || !captableInfo?.id) {
      return;
    }

    const sheetName = currentSpreadsheet.name;
    const sheetCells = sheetName ? cells?.[sheetName] : undefined;
    if (!sheetCells) {
      return;
    }

    const cellsToValidate = Object.values(sheetCells).filter(
      cell => !cell.readOnly || (cell as any).validateWhenReadOnly
    );
    const { areCellsValid: areSheetCellsValid } = validateCells({
      cellsToValidate,
      customValidations: currentSpreadsheet.customValidations,
      parsedColumns: currentSpreadsheet.cells,
      tableData: currentSpreadsheet.tableData,
      isSaving: true,
      useOriginalCell: true,
    });

    if (sheetName) {
      setCells(prev => ({
        ...prev,
        [sheetName]: { ...sheetCells },
      }));
    }

    if (!areSheetCellsValid) {
      // Errors in rows hidden by any filter cannot be seen, so show every row and say why.
      if (cellsToValidate.some(cell => cell.isValid === false && cell.isVisible === false)) {
        writeEntityParam(NO_ENTITY_FILTER);
        setResetFundSecurityFilters(true);
        enqueueSnackbar(HIDDEN_ROW_ERROR_MESSAGE, { variant: 'warning' });
      }
      return Promise.reject();
    }

    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();
    alignPreFxSigns(reversedData);
    try {
      const identity = {
        captableId: captableInfo.id,
        currency: effectiveIrrCurrency,
      };
      await updateCashFlowLedger(captableInfo.id, reversedData);
      if (!isSameLedger(currentLedgerIdentityRef.current, identity)) {
        return;
      }
      // Refresh cash flows after save to get updated IDs and resort
      const generation = ++ledgerRequestGenerationRef.current;
      const flows = await fetchCashFlowLedger(captableInfo.id);
      if (
        generation === ledgerRequestGenerationRef.current
        && isSameLedger(currentLedgerIdentityRef.current, identity)
      ) {
        setCashFlows(sortCashFlowsByDate(withEffectiveCurrency(
          flows || [],
          effectiveIrrCurrency
        )));
      }
    } catch {
      // Error is logged in the hook
      return Promise.reject();
    }
  }, [
    captableInfo?.id,
    cells,
    effectiveIrrCurrency,
    fetchCashFlowLedger,
    setCells,
    sortCashFlowsByDate,
    updateCashFlowLedger,
    validateCells,
    writeEntityParam,
    setResetFundSecurityFilters,
  ]);

  // Keep a ref to handleSave to provide a stable callback to updatePageActions
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);
  const stableHandleSave = useCallback(() => handleSaveRef.current(), []);

  // Hidden in export mode, same as the entity filter above.
  const fundSecurityFilterActionsNode = useMemo(
    () => (isExportMode ? null : (
      <>
        <FundFilterMenu cashFlows={cashFlows} setActiveFundIds={setActiveFundIds} reset={resetFundSecurityFilters} />
        <SecurityFilterMenu
          cashFlows={cashFlows}
          setActiveSecurityIds={setActiveSecurityIds}
          reset={resetFundSecurityFilters}
        />
      </>
    )),
    [cashFlows, isExportMode, resetFundSecurityFilters]
  );

  const tertiaryNavState = useMemo(() => {
    if (isEmpty(captableInfo)) {
      return null;
    }
    return {
      ariaLabel: 'Cash Flow Ledger',
      items: capTableNavItems,
      actionsNode: fundSecurityFilterActionsNode,
      actions: isAllocationFinal !== undefined ? [
        {
          type: 'button' as const,
          label: 'Save',
          variant: 'positive' as const,
          disabled: isDisabled,
          locked: isAllocationFinal,
          onClick: stableHandleSave,
        },
      ] : [],
    };
  }, [captableInfo, capTableNavItems, fundSecurityFilterActionsNode, isDisabled, isAllocationFinal, stableHandleSave]);
  useTertiaryNavigationRegistration(tertiaryNavState);

  // Register the save action only while the cash flow ledger tab owns the active page actions.
  useEffect(() => {
    if (isActiveTab && !isEmpty(captableInfo)) {
      updatePageActions?.({
        mainAction: {
          id: CASH_FLOW_LEDGER_SLUG,
          label: 'Save',
          isActive: isDisabled,
          isLock: isAllocationFinal,
          callback: () => handleSaveRef.current(),
        },
      });
    }
    return () => {
      if (isActiveTab) {
        updatePageActions?.(null);
      }
    };
  }, [
    toggleMainForm,
    togglePdfDialog,
    updatePageActions,
    captableInfo,
    companyInfo,
    isActiveTab,
    isDisabled,
    isAllocationFinal,
  ]);

  if (!spreadsheet) {
    return (
      <CustomContainer>
        <GridSkeleton />
      </CustomContainer>
    );
  }

  return (
    <CustomContainer>
      <Grid container spacing={2}>
        {/* Locked state banner */}
        {isDisabled && isAllocationFinal && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <AllocationFinalMessage
              tableTerms={{ tableName: 'Cash Flow Ledger' }}
              finalAllocations={finalAllocations}
              setIsDisabled={setIsDisabled}
              variant
              viewOnlyUser={viewOnlyUser}
            />
          </Grid>
        )}

        {/* Alert when unlocked but still final */}
        {isAllocationFinal && !isDisabled && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <Alert isAlertVisible>{CASH_FLOW_LEDGER_LOCKED_MESSAGE}</Alert>
          </Grid>
        )}

        {showEntityFilter && (
          <Grid size={{ xs: 12 }}>
            <EntityFilterChips options={entityOptions} selected={activeEntities} onToggle={toggleEntity} />
          </Grid>
        )}

        <Grid size={{ xs: 12 }} data-testid="cfl-spreadsheet" ref={setSheetContainer}>
          <CashFlowContext.Provider value={cashFlowContextValue}>
            <WorkbookContext.Provider value={workbookContextValue}>
              {cells && data && (
                <ScalarSpreadsheet
                  {...spreadsheet}
                  cells={cells}
                  showTitlesColumn={false}
                  showToolbar={false}
                  onChange={handleChange}
                  workbook={workbook}
                  sheet={spreadsheet}
                  data={data}
                  displayDeleteRowBtn={!isDisabled}
                  deleteRowFn={handleDeleteRow}
                  addMultipleRows={handleAddMultipleRows}
                  getCellIdentity={cflCellIdentity}
                  highlightSelectedRows
                  onSelectionChange={setSelectionSnapshot}
                  selectionActions={selectionActions}
                />
              )}
            </WorkbookContext.Provider>
          </CashFlowContext.Provider>
        </Grid>
        {!isExportMode && (
          <Grid size={{ xs: 12 }} sx={{ pl: '12px' }}>
            <Button
              onClick={handleAddRow}
              variant="contained"
              disabled={isDisabled}
              data-testid="cfl-add-transaction">
              Add Transaction
            </Button>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          {activeEntities.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Totals include all entities
            </Typography>
          )}
          <CashFlowSummary
            owners={owners}
            firm={firm}
            ownerNames={ownerNames}
            firmName={companyInfo?.name || firmInfo?.name || 'Total'}
          />
        </Grid>
      </Grid>
    </CustomContainer>
  );
};

export default CashFlows;
